/**
 * Salles revendiquées par un duo (2026-11-02, étage Duos de Copy Camp).
 *
 * Une bulle ou salle de réunion marquée « revendicable » dans l'éditeur
 * (`properties.claimable`) peut être revendiquée par les deux personnes qui s'y
 * trouvent, seules, au moment du clic. Jusqu'à la fin du dimanche qui vient
 * (heure de Paris), la salle et son tableau blanc ne s'ouvrent qu'à ce duo
 * (et aux OWNER/ADMIN, jamais bloqués, qui peuvent la libérer). Une personne
 * ne fait partie que d'un duo à la fois.
 * Sous-chemin `@campus/core/room-claims` côté serveur, jamais le barrel.
 */

/** Revendication active d'une salle, telle que l'envoient l'API et la salle temps réel. */
export interface RoomClaimDTO {
  id: string;
  zoneId: string;
  userIds: string[];
  expiresAt: string;
}

/** La zone (propriétés JSON) est-elle revendicable par un duo ? */
export function isClaimableZone(properties: unknown): boolean {
  return !!properties && typeof properties === 'object' && (properties as Record<string, unknown>).claimable === true;
}

const PARIS = 'Europe/Paris';

/** Décalage de Paris par rapport à UTC à cet instant, en millisecondes. */
function parisOffsetMs(at: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PARIS, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(at));
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second')) - Math.floor(at / 1000) * 1000;
}

/**
 * Fin de la revendication : minuit à Paris à la fin du dimanche qui vient
 * (23 h 59 compris). Revendiquée un dimanche, elle court jusqu'au soir même.
 */
export function claimExpiry(now: Date = new Date()): Date {
  const at = now.getTime();
  const local = new Date(at + parisOffsetMs(at)); // heure de Paris lue en UTC
  const weekday = local.getUTCDay(); // 0 = dimanche
  const daysToSunday = weekday === 0 ? 0 : 7 - weekday;
  const guess = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() + daysToSunday + 1);
  // Minuit à Paris = minuit UTC moins le décalage du moment (heure d'été comprise).
  const first = guess - parisOffsetMs(guess);
  return new Date(guess - parisOffsetMs(first));
}

/** La revendication est-elle encore en cours ? */
export function isClaimActive(claim: Pick<RoomClaimDTO, 'expiresAt'>, now = Date.now()): boolean {
  return Date.parse(claim.expiresAt) > now;
}

/** La personne peut-elle entrer dans cette salle (et ouvrir son tableau) ? */
export function canUseClaimedRoom(claim: Pick<RoomClaimDTO, 'userIds' | 'expiresAt'> | undefined, userId: string, role: string, now = Date.now()): boolean {
  if (!claim || !isClaimActive(claim, now)) return true;
  if (['OWNER', 'ADMIN'].includes(role.toUpperCase())) return true;
  return claim.userIds.includes(userId);
}

/** « dimanche 8 novembre » : dernier jour de la revendication, en heure de Paris. */
export function claimLastDay(expiresAt: string): string {
  const lastMoment = new Date(Date.parse(expiresAt) - 60_000);
  return new Intl.DateTimeFormat('fr-FR', { timeZone: PARIS, weekday: 'long', day: 'numeric', month: 'long' }).format(lastMoment);
}
